import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { 
  authUser, 
  createUser, 
  signOut, 
  getCurrentUser, 
  refreshAuth,
  signInWithGoogle,
  pb,
} from './pocketbase'
import type { User, AuthResponse } from './pocketbase'
import { Collections } from '@/types/pocketbase-types'
import type { 
  BookSummariesResponse,
  BookQuestionsResponse,
  BooksResponse,
  SectionsResponse
} from '@/types/pocketbase-types'
import type {
  UserScoreExtended,
  LeaderboardExtended,
  Question,
  ChallengeProgress,
  DashboardResponse
} from '@/types/extended-types'

// Query keys
export const queryKeys = {
  user: ['user'] as const,
  articles: ['articles'] as const,
  books: ['books'] as const,
  sections: ['sections'] as const,
  leaderboard: ['leaderboard'] as const,
  userScores: ['userScores'] as const,
  challenges: ['challenges'] as const,
  dashboard: ['dashboard'] as const,
} as const

// User query
export const useUser = () => {
  return useQuery({
    queryKey: queryKeys.user,
    queryFn: async (): Promise<User | null> => {
      const user = getCurrentUser()
      if (!user) return null
      
      // Try to refresh the auth to ensure it's still valid
      const refreshResult = await refreshAuth()
      
      // If refresh failed, return null (user is not authenticated)
      if (!refreshResult) {
        return null
      }
      
      return refreshResult.record
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // Don't retry user queries
  })
}

// Sign in mutation
export const useSignIn = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }): Promise<AuthResponse> => {
      return await authUser(email, password)
    },
    onSuccess: (data) => {
      // Update the user query cache
      queryClient.setQueryData(queryKeys.user, data.record)
      // Invalidate and refetch any other queries that depend on auth
      queryClient.invalidateQueries({ queryKey: queryKeys.articles })
      queryClient.invalidateQueries({ queryKey: queryKeys.books })
      queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard })
    },
    onError: (error) => {
      console.error('Sign in error:', error)
      // Clear any cached user data on auth error
      queryClient.setQueryData(queryKeys.user, null)
    },
  })
}

// Sign up mutation
export const useSignUp = () => {
  return useMutation({
    mutationFn: async ({
      email,
      password,
      passwordConfirm,
      name,
      username,
      birthdate,
      gender
    }: {
      email: string
      password: string
      passwordConfirm: string
      name?: string
      username?: string
      birthdate?: string
      gender?: 'male' | 'female'
    }): Promise<User> => {
      return await createUser(email, password, passwordConfirm, name, username, birthdate, gender)
    },
    onSuccess: () => {
      // Don't automatically sign in, let the user sign in manually
      // This follows the typical flow where email verification might be required
    },
    onError: (error) => {
      console.error('Sign up error:', error)
    },
  })
}

// Sign out mutation
export const useSignOut = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (): Promise<void> => {
      signOut()
    },
    onSuccess: () => {
      // Clear all cached data on sign out
      queryClient.clear()
      // Set user to null
      queryClient.setQueryData(queryKeys.user, null)
    },
  })
}

// Auto sign in mutation (for after successful registration)
export const useAutoSignIn = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }): Promise<AuthResponse> => {
      return await authUser(email, password)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.user, data.record)
      queryClient.invalidateQueries({ queryKey: queryKeys.articles })
      queryClient.invalidateQueries({ queryKey: queryKeys.books })
      queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard })
    },
  })
}

// Google OAuth sign in mutation
export const useSignInWithGoogle = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (): Promise<void> => {
      return await signInWithGoogle()
    },
    onSuccess: () => {
      // After OAuth redirect, we need to refresh the user data
      queryClient.invalidateQueries({ queryKey: queryKeys.user })
      queryClient.invalidateQueries({ queryKey: queryKeys.articles })
      queryClient.invalidateQueries({ queryKey: queryKeys.books })
      queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard })
    },
    onError: (error) => {
      console.error('Google sign in error:', error)
    },
  })
}

// ===============================
// POCKETBASE COLLECTION QUERIES
// ===============================


// Articles (sections with article content)
export const useArticles = () => {
  return useQuery({
    queryKey: queryKeys.articles,
    queryFn: async (): Promise<SectionsResponse[]> => {
      const records = await pb.collection(Collections.Sections).getFullList({
        filter: 'article != ""',
        sort: '-created',
      })
      return records as unknown as SectionsResponse[]
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Article by slug (assuming sections have slug field)
export const useArticleBySlug = (slug: string) => {
  return useQuery({
    queryKey: [...queryKeys.articles, slug],
    queryFn: async (): Promise<SectionsResponse> => {
      const record = await pb.collection(Collections.Sections).getFirstListItem(`slug="${slug}" && article != ""`)
      return record as unknown as SectionsResponse
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 10, // 10 minutes
  })
}

// Leaderboard
export const useLeaderboard = (filters?: { school?: string; class?: string }) => {
  return useQuery({
    queryKey: [...queryKeys.leaderboard, filters],
    queryFn: async (): Promise<LeaderboardExtended[]> => {
      let filter = ''
      if (filters?.school) {
        filter += `school="${filters.school}"`
      }
      if (filters?.class) {
        filter += filter ? ` && class="${filters.class}"` : `class="${filters.class}"`
      }
      
      const records = await pb.collection(Collections.Leaderboard).getFullList({
        filter,
        sort: '-weeklyScore',
        expand: 'user',
      })
      return records as unknown as LeaderboardExtended[]
    },
    staleTime: 1000 * 60 * 2, // 2 minutes (leaderboard should be fresh)
  })
}

// User scores for current user
export const useUserScores = (userId?: string) => {
  return useQuery({
    queryKey: [...queryKeys.userScores, userId],
    queryFn: async (): Promise<UserScoreExtended[]> => {
      if (!userId) return []
      
      const records = await pb.collection(Collections.UserScore).getFullList({
        filter: `user="${userId}"`,
        sort: '-created',
      })
      return records as unknown as UserScoreExtended[]
    },
    enabled: userId !== "",
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// User challenges (daily/monthly progress and streak)
export const useChallenges = (userId?: string) => {
  return useQuery({
    queryKey: [...queryKeys.challenges, userId],
    queryFn: async (): Promise<ChallengeProgress> => {
      if (!userId) {
        return {
          daily: {
            scoreProgress: 0,
            scoreTarget: 30,
            scorePercentage: 0,
            quizProgress: 0,
            quizTarget: 1,
            quizPercentage: 0,
          },
          monthly: {
            scoreProgress: 0,
            scoreTarget: 500,
            scorePercentage: 0,
            quizProgress: 0,
            quizTarget: 20,
            quizPercentage: 0,
            perfectScoresProgress: 0,
            perfectScoresTarget: 10,
            perfectScoresPercentage: 0,
          },
          streak: 0,
        }
      }

      const response = await fetch(`${pb.baseUrl}/api/challenges`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${pb.authStore.token}`,
        },
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch challenges')
      }
      
      return response.json()
    },
    enabled: userId !== "",
    staleTime: 1000 * 60 * 2, // 2 minutes (challenges should be relatively fresh)
  })
}

// ===============================
// BOOK SEARCH MUTATIONS
// ===============================


// ===============================
// QUIZ SUBMISSION MUTATIONS
// ===============================

export interface QuizAnswer {
  questionId: string
  selectedAnswer: number
  isCorrect: boolean
  timeSpent: number
}

export interface ArticleQuizSubmission {
  summaryId: string
  answers: QuizAnswer[]
}

export interface BookQuizSubmission {
  bookSummaryId: string
  answers: QuizAnswer[]
}

// Article quiz submission (replaces /api/quiz with type="article")
export const useArticleQuizSubmission = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ summaryId, answers }: ArticleQuizSubmission) => {
      const response = await fetch(`${pb.baseUrl}/api/quiz/article/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${pb.authStore.token}`,
        },
        body: JSON.stringify({ summaryId, answers }),
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Quiz submission failed')
      }
      
      return response.json()
    },
    onSuccess: () => {
      // Invalidate user scores to reflect new quiz result
      queryClient.invalidateQueries({ queryKey: queryKeys.userScores })
      queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard })
    },
  })
}

// Book quiz submission (replaces /api/quiz with type="book")
export const useBookQuizSubmission = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ bookSummaryId, answers }: BookQuizSubmission) => {
      const response = await fetch(`${pb.baseUrl}/api/quiz/book/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${pb.authStore.token}`,
        },
        body: JSON.stringify({ bookSummaryId, answers }),
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Quiz submission failed')
      }
      
      return response.json()
    },
    onSuccess: () => {
      // Invalidate user scores to reflect new quiz result
      queryClient.invalidateQueries({ queryKey: queryKeys.userScores })
      queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard })
    },
  })
}

// ===============================
// BOOKS TABLE QUERIES
// ===============================

export interface BookSearchParams {
  title?: string
  author?: string
  isbn?: string
}

export interface BookSearchResult {
  data: BooksResponse
  created: boolean
  isExisting?: boolean
}

// Book search mutation - checks for existing processed books first
export const useBookSearch = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ title, author, isbn }: BookSearchParams): Promise<BookSearchResult> => {
      // Validate input
      if (!isbn && (!title || !author)) {
        throw new Error('Either ISBN or both title and author are required')
      }

        // First, try to find existing processed book
        let existingBook: BooksResponse | null = null
        
        if (isbn) {
          try {
            existingBook = await pb.collection(Collections.Books)
              .getFirstListItem(`isbn="${isbn}" && phase="processed"`) as unknown as BooksResponse
          } catch (error: any) {
            // Book not found, continue to create new one
          }
        } else if (title && author) {
          try {
            // Search for books with matching title and author (case-insensitive partial match)
            existingBook = await pb.collection(Collections.Books)
              .getFirstListItem(`(title~"${title}" || user_title~"${title}") && author~"${author}" && phase="processed"`) as unknown as BooksResponse
          } catch (error: any) {
            // Book not found, continue to create new one
          }
        }

        // If existing processed book found, return it
        if (existingBook) {
          return {
            data: existingBook,
            created: false,
            isExisting: true
          }
        }

        // No existing processed book found, create new record for search
        const bookData: Partial<BooksResponse> = {
          phase: 'initial' as any,
          action: 'search' as any,
          retry_count: 0,
          search_completed: false,
          metadata_downloaded: false,
          cover_downloaded: false,
          epub_downloaded: false,
          summarized: false,
          questions_generated: false,
        }

        if (isbn) {
          bookData.isbn = isbn
        } else {
          bookData.user_title = title
          bookData.author = author
        }

        const createdBook = await pb.collection(Collections.Books).create(bookData) as unknown as BooksResponse

        return {
          data: createdBook,
          created: true,
          isExisting: false
        }
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.books })
    },
  })
}

// Book by slug with full data (book, summary, questions)
export const useBookBySlug = (slug: string) => {
  return useQuery({
    queryKey: [...queryKeys.books, slug],
    queryFn: async (): Promise<{
      book: BooksResponse | null
      summary?: BookSummariesResponse
      questions?: Question[]
      isProcessing?: boolean
    }> => {
      try {
        // Get book from unified books table
        const book = await pb.collection(Collections.Books)
          .getFirstListItem(`slug="${slug}"`, { sort: '-created' }) as unknown as BooksResponse
        
        // If book is still processing, return it with processing flag
        if (book.phase !== 'processed') {
          return { book, isProcessing: true }
        }
        
        try {
          // Try to get summary using new 'book' relation field
          const summary = await pb.collection(Collections.BookSummaries)
            .getFirstListItem(`book="${book.id}"`) as unknown as BookSummariesResponse
          
          const questionsResponse = await pb.collection(Collections.BookQuestions).getList(1, 50, {
            filter: `book_summary="${summary.id}"`,
            sort: '@random',
          }) as unknown as { items: BookQuestionsResponse[] }
          
          // Format questions
          const loQuestions = questionsResponse.items.filter((q) => q.type === 'LO').slice(0, 0)
          const mcqQuestions = questionsResponse.items.filter((q) => q.type === 'MCQ').slice(0, 15)
          const limitedQuestions = [...loQuestions, ...mcqQuestions]
          
          const formattedQuestions: Question[] = limitedQuestions.map((q) => ({
            id: q.id,
            type: q.type as 'MCQ' | 'LO', // Explicit type casting
            question: q.question,
            options: q.type === 'MCQ' ? (q.choices as string[] || []) : [],
            correctAnswer: q.type === 'MCQ' ? q.answer || '' : '',
            openEndedAnswers: q.type === 'LO' ? (q.lo_answers as string[] || []) : [],
          }))
          
          return { book, summary, questions: formattedQuestions }
        } catch (summaryError: any) {
          // Summary/questions not ready yet, but book is processed
          // This might be a race condition, return as processing
          return { book, isProcessing: true }
        }
      } catch (error: any) {
        console.error('Error fetching unified book:', error)
        if (error?.status === 404) {
          return { book: null }
        }
        throw error
      }
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: (failureCount, error: any) => {
      // Only retry if it's a 404 error and we haven't retried too many times
      if (error?.status === 404 && failureCount < 3) {
        return true
      }
      return false
    },
  })
}

// Books list
export const useBooks = () => {
  return useQuery({
    queryKey: queryKeys.books,
    queryFn: async (): Promise<BooksResponse[]> => {
      const records = await pb.collection(Collections.Books).getFullList({
        sort: '-created',
      })
      return records as unknown as BooksResponse[]
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Book action trigger (download, process, retry, reset)
export const useBookAction = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ bookId, action }: { bookId: string, action: string }) => {
      const updatedBook = await pb.collection(Collections.Books).update(bookId, { action })
      return updatedBook as unknown as BooksResponse
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.books })
    },
  })
}

// ===============================
// USER PROFILE MUTATIONS
// ===============================

// Update user avatar
export const useUpdateUserAvatar = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ userId, avatarBlob }: { userId: string; avatarBlob: Blob }) => {
      const formData = new FormData()
      formData.append('avatar', avatarBlob, `avatar_${userId}_${Date.now()}.png`)
      
      const updatedUser = await pb.collection('users').update(userId, formData)
      return updatedUser as unknown as User
    },
    onSuccess: (updatedUser) => {
      // Update the user query cache with the new avatar
      queryClient.setQueryData(queryKeys.user, updatedUser)
      
      // Invalidate leaderboard as it may show user avatars
      queryClient.invalidateQueries({ queryKey: queryKeys.leaderboard })
    },
    onError: (error) => {
      console.error('Avatar update error:', error)
    },
  })
}

// Dashboard query - single API call for all dashboard data
export const useDashboard = (userId?: string) => {
  return useQuery({
    queryKey: [...queryKeys.dashboard, userId],
    queryFn: async (): Promise<DashboardResponse> => {
      if (!userId) {
        throw new Error('User ID is required')
      }

      const response = await fetch(`${pb.baseUrl}/api/dashboard`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${pb.authStore.token}`,
        },
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data')
      }
      
      return response.json()
    },
    enabled: userId !== "",
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes cache
  })
}